// Skills v2 — Quality & Improve, the unified closed-loop surface (2026-08-23).
//
// Conformance spec: docs/design/skills-framework-v2-mockup.html <section id="s7">.
// A SUPERSET of the mockup — review bar · 4 metric tiles · four-signal strip ·
// outcome-first proposal cards (with real approve / undo / preview-diff) ·
// per-skill health table · the air-gap "deep learning" card (real availability +
// execute). One aggregated fetch via useAgentSkills (A2/A4); all shaping logic
// lives in ./qualityFormat + ./qualityImproveFormat (pure, unit-tested).

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { COLORS } from "../../../theme/colors";
import { MATERIALS } from "../../../theme/materials";
import { skillsApi, observabilityApi } from "../../../api/client";
import { useAgentSkills } from "../../../hooks/useAgentSkills";
import SearchableSelect from "../../../components/SearchableSelect";
import { signalReadings, healthLabel, formatPct, toneColor } from "./qualityFormat";
import {
  proposalKind, deltaPct, proposalHeadline, whyYouDecide, developerNote,
  perSkillRows, deriveMetrics, signPct, timeAgo,
  nextActionRows, datasetOptions, resolveDefaultDataset, isSuggestedDefault,
} from "./qualityImproveFormat";

function unwrapList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}
async function safe(fn, fallback) {
  try { return await fn(); } catch { return fallback; }
}

const CARD = () => ({
  border: `1px solid ${COLORS.border}`, borderRadius: 16, background: COLORS.surface,
  boxShadow: MATERIALS.spatialShadow(), padding: "14px 15px",
});
const SECT_LABEL = {
  fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
  color: COLORS.textDim, margin: "18px 0 10px", display: "flex", alignItems: "center", gap: 8,
};
const btn = (variant) => ({
  font: "inherit", fontSize: variant === "sm" ? 12 : 13, fontWeight: variant === "ghost" || variant === "sm" ? 600 : 700,
  color: variant === "ghost" || variant === "sm" ? COLORS.textMuted : "#fff",
  background: variant === "ghost" || variant === "sm" ? COLORS.surface : COLORS.accent,
  border: variant === "ghost" || variant === "sm" ? `1px solid ${COLORS.border}` : "none",
  padding: variant === "sm" ? "7px 12px" : "9px 15px", borderRadius: 9, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 7,
  boxShadow: variant === "ghost" || variant === "sm" ? "none" : `0 0 16px ${COLORS.accentGlow}`,
});
const selBox = {
  font: "inherit", fontSize: 12.5, color: COLORS.text, background: COLORS.surfaceHover,
  border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 10px",
};

function Pill({ label, tone, prefix }) {
  const color = toneColor(tone, COLORS);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 700,
      padding: "2px 8px", borderRadius: 999, color, background: `${color}1F`, border: `1px solid ${color}66`,
    }}>{prefix ?? (tone === "warn" ? "▲" : "●")} {label}</span>
  );
}
function LiftBadge({ pct, tone = "ok" }) {
  const color = toneColor(tone, COLORS);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 700,
      padding: "3px 9px", borderRadius: 999, color, background: `${color}24`, border: `1px solid ${color}66`,
    }}>{signPct(pct)}</span>
  );
}
function Metric({ label, value, color }) {
  return (
    <div style={{ ...CARD(), borderRadius: 12, padding: "13px 15px" }}>
      <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.05em", color: COLORS.textDim, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 750, marginTop: 4, fontVariantNumeric: "tabular-nums", color: color || COLORS.text }}>{value}</div>
    </div>
  );
}
function SignalCard({ card }) {
  const color = toneColor(card.tone, COLORS);
  const big = card.unit === "%" ? formatPct(card.value) : String(card.value);
  return (
    <div style={{ ...CARD(), padding: "12px 13px" }}>
      <div style={{ fontSize: 12, color: COLORS.textDim }}>{card.icon} {card.label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color, marginTop: 2 }}>
        <span>{big}</span>
        {card.unit === "kept" && <span style={{ fontSize: 12, color: COLORS.textDim, fontWeight: 600 }}> kept</span>}
      </div>
    </div>
  );
}
function DiffBlock({ before, after }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
      <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "9px 11px", background: COLORS.surfaceHover }}>
        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, color: COLORS.pink, marginBottom: 5 }}>Before</div>
        <p style={{ margin: 0, fontSize: 12.5, color: COLORS.text, lineHeight: 1.45 }}>{String(before ?? "—")}</p>
      </div>
      <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "9px 11px", background: COLORS.surfaceHover }}>
        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, color: COLORS.green, marginBottom: 5 }}>After</div>
        <p style={{ margin: 0, fontSize: 12.5, color: COLORS.text, lineHeight: 1.45 }}>{String(after ?? "—")}</p>
      </div>
    </div>
  );
}

// One outcome-first improvement proposal card (awaiting-your-okay OR applied).
function ProposalCard({ p, now, onApply, onUndo, busy }) {
  const [showDiff, setShowDiff] = useState(false);
  const [detail, setDetail] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const kind = proposalKind(p);
  const d = deltaPct(p);
  const headline = proposalHeadline(p);
  const vAfter = p.version_after || "next";
  const payload = (detail && detail.draft_payload) || p.draft_payload || {};
  const before = payload.before ?? payload.old ?? payload._previous_body ?? null;
  const after = payload.after ?? payload.new ?? payload.new_body ?? null;

  const togglePreview = async () => {
    const opening = !showDiff;
    setShowDiff(opening);
    if (opening && !detail) {
      try {
        const full = await skillsApi.getProposal(p.id);
        setDetail(full && full.data ? full.data : full);
      } catch { /* fall back to the list payload */ }
    }
  };

  if (kind === "applied") {
    return (
      <div style={{ ...CARD(), borderColor: `${COLORS.green}55` }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, display: "grid", placeItems: "center", fontSize: 17, background: `${COLORS.green}1F`, border: `1px solid ${COLORS.green}66`, flex: "none" }}>✓</div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <strong style={{ fontSize: 14, color: COLORS.text }}>Applied for you</strong>
              <Pill label="done automatically" tone="ok" />
            </div>
            <div style={{ fontSize: 12.5, color: COLORS.textMuted, marginTop: 4, maxWidth: "64ch" }}>{headline}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
              {d != null && <LiftBadge pct={d} tone="ok" />}
              <span style={{ fontSize: 11.5, color: COLORS.textDim }}>Applied {timeAgo(p.applied_at, now)} · v{vAfter}</span>
              <div style={{ flex: 1 }} />
              <button style={btn("sm")} onClick={togglePreview}>See what changed</button>
              <button style={btn("sm")} disabled={busy} onClick={() => onUndo(p)} title="Restore the version from before this change">
                {busy ? "Undoing…" : "↩ Undo"}
              </button>
            </div>
            {showDiff && (before != null || after != null) && <DiffBlock before={before} after={after} />}
            <div style={{ fontSize: 11.5, color: COLORS.textDim, marginTop: 8 }}>Developer view: {developerNote(p)}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...CARD(), borderColor: `${COLORS.orange}55` }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, display: "grid", placeItems: "center", fontSize: 17, background: `${COLORS.orange}1F`, border: `1px solid ${COLORS.orange}66`, flex: "none" }}>📉</div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <strong style={{ fontSize: 14, color: COLORS.text }}>{headline}</strong>
            <Pill label="needs your okay" tone="warn" />
          </div>
          <div style={{ fontSize: 12.5, color: COLORS.textMuted, marginTop: 4, maxWidth: "64ch" }}>
            We found a fix and measured it on your golden set
            {d != null && <>: <strong style={{ color: COLORS.green }}>{signPct(d)} accuracy</strong></>}.
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 10, fontSize: 12.5, color: COLORS.textMuted, background: COLORS.accentSoft, border: `1px solid ${COLORS.accent}`, borderRadius: 10, padding: "8px 11px" }}>
            <span>🔎</span><div><strong style={{ color: COLORS.text }}>Why you decide this one:</strong> {whyYouDecide(p)}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <button style={btn("ghost")} onClick={togglePreview}>Preview the change</button>
            {confirming ? (
              <>
                <button style={btn()} disabled={busy} onClick={() => onApply(p)}>{busy ? "Applying…" : "Confirm apply"}</button>
                <button style={btn("ghost")} disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
              </>
            ) : (
              <button style={btn()} onClick={() => setConfirming(true)}>Approve &amp; apply</button>
            )}
            <span style={{ fontSize: 11.5, color: COLORS.textDim }}>Will save as v{vAfter} · one-tap undo</span>
          </div>
          {showDiff && (
            (before != null || after != null)
              ? <DiffBlock before={before} after={after} />
              : <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 10 }}>Preview: {developerNote(p)} (full diff opens in the developer view).</div>
          )}
          <div style={{ fontSize: 11.5, color: COLORS.textDim, marginTop: 8 }}>Developer view: {developerNote(p)}</div>
        </div>
      </div>
    </div>
  );
}

export default function QualitySection({ agentId }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAdmin = ["owner", "administrator"].includes(currentUser?.role);
  const { data, loading, error, reload } = useAgentSkills(agentId);
  const suggestedDataset = (data && data.suggested_dataset) || null;
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);
  // Manual-trigger controls (the loop is normally cron-driven).
  const [datasets, setDatasets] = useState([]);
  const [runDataset, setRunDataset] = useState("");
  const datasetTouched = useRef(false);
  const [runLayer, setRunLayer] = useState("skills");
  const [runBusy, setRunBusy] = useState(null);
  const [runMsg, setRunMsg] = useState(null);
  // Air-gap consolidation execution.
  const [dlBusy, setDlBusy] = useState(false);
  const [dlMsg, setDlMsg] = useState(null);
  // Tenant opt-in consent switch (distinct from the run action below).
  const [optBusy, setOptBusy] = useState(false);
  const proposalsRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const ds = unwrapList(await safe(() => observabilityApi.listDatasets(), []));
      if (alive) setDatasets(ds);
    })();
    return () => { alive = false; };
  }, [agentId]);

  // Default the "Run a check now" dataset — prefer the agent's most-recent
  // eval-run dataset (suggested_dataset), then the first dataset — until the
  // user picks one. Re-runs when either the dataset list or the suggestion
  // arrives (they load from two independent fetches).
  useEffect(() => {
    if (datasetTouched.current) return;
    setRunDataset((prev) => resolveDefaultDataset(suggestedDataset, datasets, prev));
  }, [datasets, suggestedDataset]);

  const flash = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast((t) => (t === msg ? null : t)), 4000);
  }, []);

  const onApply = async (p) => {
    setBusyId(p.id);
    try {
      await safe(() => skillsApi.applyProposal(p.id), null);
      flash("Applied ✓ — you can undo it any time.");
      await reload();
    } finally { setBusyId(null); }
  };
  const onUndo = async (p) => {
    setBusyId(p.id);
    try {
      const res = await skillsApi.revertProposal(p.id);
      if (res && res.success === false) flash("Couldn't undo — it may already be reverted.");
      else flash("Undone ✓ — restored the previous version.");
      await reload();
    } catch (e) {
      flash(e?.message || "Couldn't undo this change.");
    } finally { setBusyId(null); }
  };

  const runImprovement = async () => {
    if (!runDataset) { setRunMsg("Pick a golden-set dataset first."); return; }
    setRunBusy("improve"); setRunMsg(null);
    try {
      await skillsApi.runImprovement(agentId, { target_layer: runLayer, trigger: "manual", dataset_id: runDataset });
      setRunMsg("Check complete — new proposals appear below.");
      await reload();
    } catch (e) { setRunMsg(e?.message || "Couldn't run the check."); }
    finally { setRunBusy(null); }
  };
  const runFlywheel = async () => {
    if (!runDataset) { setRunMsg("Pick a golden-set dataset first."); return; }
    setRunBusy("flywheel"); setRunMsg(null);
    try {
      await skillsApi.runFlywheel(agentId, { dataset_id: runDataset });
      setRunMsg("Re-measured standing lift for every attached skill.");
      await reload();
    } catch (e) { setRunMsg(e?.message || "Couldn't re-check lift."); }
    finally { setRunBusy(null); }
  };
  const turnOnDeepLearning = async () => {
    setDlBusy(true); setDlMsg(null);
    try {
      const res = await skillsApi.consolidateRun(agentId);
      const r = res && res.data ? res.data : res;
      setDlMsg(r?.ran ? "In-boundary consolidation started — it eval-gates before any deploy." : (r?.reason || "Not available in this environment."));
    } catch (e) { setDlMsg(e?.message || "Couldn't start consolidation."); }
    finally { setDlBusy(false); }
  };

  const setConsolidationOptIn = async (enabled) => {
    setOptBusy(true); setDlMsg(null);
    try {
      await skillsApi.consolidateOptIn(agentId, enabled);
      await reload();  // refreshes consolidation.{opt_in, available, reason}
    } catch (e) {
      setDlMsg(e?.message || "Couldn't update the opt-in.");
    } finally { setOptBusy(false); }
  };

  if (loading) return <div style={CARD()}><div style={{ fontSize: 13, color: COLORS.textDim }}>Loading quality…</div></div>;
  if (error) return <div style={CARD()}><div style={{ fontSize: 13, color: toneColor("warn", COLORS) }}>{error}</div></div>;

  const d = (data && data.quality) || {};
  const proposals = (data && data.proposals) || [];
  const perSkill = perSkillRows((data && data.per_skill) || [], agentId);
  const consolidation = (data && data.consolidation) || null;
  const health = healthLabel(d.health, d.decayed);
  const insufficient = d.health === "insufficient_data";
  const cards = signalReadings(d.by_type);
  const now = Date.now();
  const metrics = deriveMetrics({ perSkill, proposals, now });
  const actionable = proposals.filter((p) => proposalKind(p) !== "other");
  const awaitingProps = actionable.filter((p) => proposalKind(p) === "awaiting");
  const nextActions = nextActionRows((data && data.next_actions) || []);
  // Build the action rows as a statement (not an inline JSX .map) so the map
  // line executes on every render regardless of how v8 attributes JSX
  // sub-expressions to source lines across node versions (diff-coverage trap).
  const accentColor = COLORS.accentText || COLORS.accent;
  const actionEls = nextActions.map((a, i) => (
    <div key={a.code || i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
      <span style={{ color: accentColor, flex: "none" }}>→</span>
      <div>
        <strong style={{ color: COLORS.text }}>{a.label}</strong>
        {a.detail ? <span style={{ color: COLORS.textDim }}> — {a.detail}</span> : null}
      </div>
    </div>
  ));

  const noDatasets = datasets.length === 0;
  const dsOptions = datasetOptions(datasets, suggestedDataset);
  const suggestedHint = isSuggestedDefault(suggestedDataset, runDataset);

  return (
    <div style={{ display: "flex", flexDirection: "column", position: "relative" }}>
      {toast && (
        <div style={{
          position: "sticky", top: 8, alignSelf: "flex-end", zIndex: 5,
          background: COLORS.surface, border: `1px solid ${COLORS.green}66`, color: COLORS.text,
          borderRadius: 10, padding: "8px 12px", fontSize: 12.5, boxShadow: MATERIALS.spatialShadow(),
        }}>{toast}</div>
      )}

      <div>
        <h2 style={{ fontSize: 16, margin: 0, color: COLORS.text }}>Quality &amp; improvement</h2>
        <p style={{ margin: "4px 0 0", color: COLORS.textDim, fontSize: 13, maxWidth: "66ch" }}>
          How this agent is doing — and what to change next. One surface: it watches four kinds of
          feedback, proves every fix on your own data, and either applies the safe ones for you or
          hands you the rest to approve. No verdict logs.
        </p>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <Pill label={health.label} tone={health.tone} />
        {d.decayed && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 650,
            padding: "4px 10px", borderRadius: 999, color: toneColor("warn", COLORS),
            background: `${toneColor("warn", COLORS)}1F`, border: `1px solid ${toneColor("warn", COLORS)}66`,
          }}>▲ lift decayed — review</span>
        )}
      </div>

      {/* Run a check now — the manual trigger for the loop */}
      <div style={{ ...CARD(), marginTop: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12.5, color: COLORS.text, fontWeight: 600 }}>Run a check now</span>
        {noDatasets ? (
          <span style={{ fontSize: 12, color: COLORS.textDim }}>
            — a check runs the agent over a <b style={{ color: COLORS.text }}>golden set</b> and measures the lift.{" "}
            <button type="button" onClick={() => navigate("/analytics/observability/datasets")}
              style={{ background: "none", border: "none", padding: 0, font: "inherit", cursor: "pointer", color: COLORS.accentText || COLORS.accent, fontWeight: 650 }}>Create one in Observability → Datasets →</button>
          </span>
        ) : (
          <>
            <span style={{ fontSize: 11.5, color: COLORS.textDim }}>on</span>
            <SearchableSelect
              idPrefix="qi-dataset"
              placeholder="Search datasets…"
              options={dsOptions}
              value={runDataset}
              onChange={(id) => { datasetTouched.current = true; setRunDataset(id); }}
              minWidth={180}
            />
            {suggestedHint && (
              <span style={{ fontSize: 11, color: COLORS.textDim, fontStyle: "italic" }}>
                defaulted to this agent&apos;s eval set
              </span>
            )}
            <select value={runLayer} onChange={(e) => setRunLayer(e.target.value)} style={selBox}>
              <option value="skills">Skills</option>
              <option value="prompt">Prompt</option>
              <option value="model">Model</option>
            </select>
            <button style={btn("sm")} disabled={!!runBusy} onClick={runImprovement}>
              {runBusy === "improve" ? "Checking…" : "✦ Check for an improvement"}
            </button>
            <button style={btn("sm")} disabled={!!runBusy} onClick={runFlywheel}>
              {runBusy === "flywheel" ? "Measuring…" : "↻ Re-check skill lift"}
            </button>
            {runMsg && <span style={{ fontSize: 11.5, color: COLORS.textDim }}>{runMsg}</span>}
          </>
        )}
      </div>

      {insufficient ? (
        <div style={{ ...CARD(), marginTop: 14, fontSize: 12.5, color: COLORS.textMuted }}>
          Not enough feedback yet to grade this agent. Quality signals appear here once it handles
          real conversations.
          {actionEls.length > 0 ? (
            <div style={{ marginTop: 12, display: "grid", gap: 8 }}>{actionEls}</div>
          ) : null}
        </div>
      ) : (
        <>
          {metrics.awaiting > 0 && (
            <div style={{
              display: "flex", alignItems: "center", gap: 12, border: `1px solid ${COLORS.accent}`,
              background: COLORS.accentSoft, borderRadius: 12, padding: "12px 15px", marginTop: 16,
            }}>
              <span style={{ fontSize: 18 }}>🔔</span>
              <div style={{ fontSize: 13, color: COLORS.textMuted }}>
                <strong style={{ color: COLORS.text }}>{metrics.awaiting} thing{metrics.awaiting === 1 ? "" : "s"} need{metrics.awaiting === 1 ? "s" : ""} your okay</strong>
                {awaitingProps[0] && <> — {proposalHeadline(awaitingProps[0]).toLowerCase()}{awaitingProps.length > 1 ? `, and ${awaitingProps.length - 1} more` : ""}.</>}
              </div>
              <div style={{ flex: 1 }} />
              <button style={btn("sm")} onClick={() => proposalsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}>Review now</button>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 11, marginTop: 16 }}>
            <Metric label="Avg accuracy lift" value={metrics.avgLift == null ? "—" : signPct(metrics.avgLift)} color={metrics.avgLift != null && metrics.avgLift >= 0 ? COLORS.green : COLORS.text} />
            <Metric label="Skills healthy" value={`${metrics.healthy[0]} / ${metrics.healthy[1]}`} />
            <Metric label="Applied for you (30d)" value={metrics.applied} color={metrics.applied > 0 ? COLORS.green : COLORS.text} />
            <Metric label="Awaiting your okay" value={metrics.awaiting} color={metrics.awaiting > 0 ? (COLORS.accentText || COLORS.accent) : COLORS.text} />
          </div>

          <div style={SECT_LABEL}>
            What the feedback says
            <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400, color: COLORS.textDim }}>— last 30 days</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 11 }}>
            {cards.map((card) => <SignalCard key={card.key} card={card} />)}
          </div>

          <div style={SECT_LABEL} ref={proposalsRef}>Suggested improvements</div>
          {actionable.length === 0 ? (
            <div style={{ ...CARD(), fontSize: 12.5, color: COLORS.textMuted }}>
              Improvements will appear here as the loop finds and proves them.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 11 }}>
              {actionable.map((p) => (
                <ProposalCard key={p.id} p={p} now={now} onApply={onApply} onUndo={onUndo} busy={busyId === p.id} />
              ))}
            </div>
          )}

          <div style={SECT_LABEL}>Per-skill health</div>
          {perSkill.length === 0 ? (
            <div style={{ fontSize: 12.5, color: COLORS.textDim }}>No skills active on this agent yet.</div>
          ) : (
            <div style={{ ...CARD(), padding: 0, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 460 }}>
                  <thead>
                    <tr>
                      {["Skill", "Lift now", "Trend", "Status"].map((h) => (
                        <th key={h} style={{ textAlign: "left", fontSize: 10, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textDim, fontWeight: 700, padding: "9px 15px", borderBottom: `1px solid ${COLORS.border}`, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {perSkill.map((s, i) => (
                      <tr key={i} style={{ borderBottom: i < perSkill.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                        <td style={{ padding: "12px 15px", color: COLORS.text, fontWeight: 600 }}>{s.name}</td>
                        <td style={{ padding: "12px 15px" }}>{s.liftPct == null ? <span style={{ color: COLORS.textDim }}>—</span> : <LiftBadge pct={s.liftPct} tone={s.tone === "warn" ? "warn" : "ok"} />}</td>
                        <td style={{ padding: "12px 15px", fontSize: 12, color: s.needsReview ? COLORS.orange : COLORS.textDim }}>
                          {s.needsReview ? "▼ decayed" : "▬ steady"}
                        </td>
                        <td style={{ padding: "12px 15px" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            <Pill label={s.status} tone={s.tone} prefix={s.tone === "ok" ? "●" : "▲"} />
                            {s.needsReview && (
                              <button
                                type="button"
                                onClick={() => proposalsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                                title="Review the suggested fixes"
                                style={{ background: "none", border: "none", padding: 0, font: "inherit", fontSize: 11.5, cursor: "pointer", color: COLORS.accentText || COLORS.accent, fontWeight: 650 }}
                              >Review →</button>
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Deep learning — air-gap only (availability advertised by the server) */}
          <div style={SECT_LABEL}>Deep learning <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400, color: COLORS.textDim }}>— air-gap deployments add this</span></div>
          <div style={{ ...CARD(), display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, display: "grid", placeItems: "center", fontSize: 17, background: COLORS.surfaceHover, border: `1px solid ${COLORS.border}`, flex: "none" }}>🔒</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <strong style={{ fontSize: 14, color: COLORS.text }}>Teach your own model — in your boundary</strong>
                  <Pill label={consolidation?.available ? "Available" : "Off"} tone={consolidation?.available ? "ok" : "dim"} prefix={consolidation?.available ? "●" : "○"} />
                </div>
                <div style={{ fontSize: 12.5, color: COLORS.textMuted, marginTop: 4, maxWidth: "66ch" }}>
                  Everything above works on every deployment. Air-gapped deployments unlock one more level:
                  fold the fixes you've already proven into your own local model so it just knows them —
                  freeing up its context. Nothing ever leaves your network. This is the agent's Fine-Tune,
                  right here — no separate screen.
                </div>
                {consolidation && !consolidation.available && consolidation.reason && (
                  <div style={{ fontSize: 12, color: COLORS.orange || "#d97706", marginTop: 6 }}>Not available here: {consolidation.reason}</div>
                )}
                {consolidation?.available && (
                  <div style={{ fontSize: 12, color: COLORS.green || "#10b981", marginTop: 6 }}>
                    ✓ On this agent's local model — {consolidation.exemplar_count != null ? `${consolidation.exemplar_count} proven lessons ready` : "ready to consolidate"}.
                  </div>
                )}
                {dlMsg && <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 6 }}>{dlMsg}</div>}
              </div>
            </div>

            {/* Tenant consent switch — the OPT-IN. Distinct from the run action
                below: this records your workspace's permission; the button
                further down executes a consolidation once it's allowed. */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
              padding: "11px 12px", borderRadius: 12, background: COLORS.surfaceHover,
              border: `1px solid ${COLORS.border}`,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>
                  Allow in-boundary fine-tuning
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 3, maxWidth: "60ch" }}>
                  Your workspace's consent to fold proven lessons into your local model — zero egress, never a cloud trainer.
                  {" "}
                  {!isAdmin
                    ? "Only an administrator can change this."
                    : consolidation?.opt_in && consolidation?.capability === false
                      ? "Recorded — takes effect on air-gapped deployments where the capability is granted."
                      : consolidation?.opt_in && consolidation?.local_model === false
                        ? "Recorded — point this agent at a local model to consolidate."
                        : "Turning this off blocks any future consolidation."}
                </div>
              </div>
              <button
                role="switch"
                aria-checked={!!consolidation?.opt_in}
                aria-label="Allow in-boundary fine-tuning"
                disabled={!isAdmin || optBusy}
                onClick={() => setConsolidationOptIn(!consolidation?.opt_in)}
                title={isAdmin ? "Toggle your workspace's in-boundary fine-tuning consent" : "Administrator only"}
                style={{
                  flex: "none", width: 46, height: 26, borderRadius: 999, position: "relative",
                  border: `1px solid ${consolidation?.opt_in ? (COLORS.green || "#10b981") : COLORS.border}`,
                  background: consolidation?.opt_in ? (COLORS.green || "#10b981") : COLORS.surface,
                  cursor: !isAdmin || optBusy ? "not-allowed" : "pointer",
                  opacity: !isAdmin || optBusy ? 0.6 : 1, transition: "background .15s, border-color .15s",
                }}
              >
                <span style={{
                  position: "absolute", top: 2, left: consolidation?.opt_in ? 22 : 2,
                  width: 20, height: 20, borderRadius: "50%", background: "#fff",
                  boxShadow: "0 1px 2px rgba(0,0,0,.3)", transition: "left .15s",
                }} />
              </button>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12, color: COLORS.textDim }}>
              {[
                `1 · Gather ${consolidation?.exemplar_count != null ? `${consolidation.exemplar_count} ` : ""}proven lessons`,
                "2 · Train the local model in-boundary", "3 · Eval-gate (must beat today)", "4 · A/B, then deploy",
              ].map((step, i) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 999, background: COLORS.surfaceHover, border: `1px solid ${COLORS.border}` }}>
                  {step}{i < 3 ? <span style={{ opacity: 0.5 }}>→</span> : null}
                </span>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <button
                style={consolidation?.available ? btn() : { ...btn("ghost"), cursor: "not-allowed", opacity: 0.7 }}
                disabled={!consolidation?.available || dlBusy}
                onClick={turnOnDeepLearning}
                title={consolidation?.available ? "Start in-boundary consolidation" : (consolidation?.reason || "Offered only where a local model can learn")}
              >{dlBusy ? "Starting…" : "Turn on deep learning"}</button>
              <span style={{ fontSize: 11.5, color: COLORS.textDim }}>Only offered where a local model can learn · zero cloud fine-tuning, ever.</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
